import type { PatientsSearchT } from "@/routes/_app/patients";
import { useAuth } from "@/utils/auth";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import {
    diariesByPatientQueryOptions,
    diariesBySettingsQueryOptions,
    diariesTabsQueryOptions,
    diaryByIdQueryOptions,
    otherTabCountsQueryOptions,
    patientDiarySearchQueryOptions,
} from "./diaryQueryOptions";
import { useAppLocale } from "@/features/i18n/LocaleProvider";

// ---------------------------------------------------------------------------
// Patients-page hooks
// ---------------------------------------------------------------------------

/** Paginated diary rows + current-tab count for the patients table.
 *  Uses useQuery (not useSuspenseQuery) so that `keepPreviousData` works —
 *  the old rows stay visible while a new sort/page loads instead of flashing
 *  a skeleton. The route loader prefetches the initial data so the first
 *  render is always instant. */
export const useDiariesBySettingsQuery = (settings: PatientsSearchT) => {
    const auth = useAuth();
    return useQuery(diariesBySettingsQueryOptions(settings, auth));
};

/** Counts for the two non-current tabs (current tab count comes from useDiariesBySettingsQuery). */
export const useOtherTabCountsQuery = (settings: PatientsSearchT) => {
    const auth = useAuth();
    return useQuery(otherTabCountsQueryOptions(settings, auth));
};

/** Server-side patient search for the search dialog. */
export const usePatientDiarySearchQuery = (query: string, enabled: boolean) => {
    return useQuery(patientDiarySearchQueryOptions(query, enabled));
};

// ---------------------------------------------------------------------------
// General diary hooks (used outside the patients page)
// ---------------------------------------------------------------------------

export const useDiaryByIdQuery = (diaryId: string) => {
    const { locale } = useAppLocale();
    return useSuspenseQuery(diaryByIdQueryOptions(diaryId, locale));
};

/** Non-suspense diary fetch for print preview when diary data is already embedded in the route. */
export const useDiaryByIdQueryWhenNeeded = (diaryId: string, enabled: boolean) => {
    const { locale } = useAppLocale();
    return useQuery({
        ...diaryByIdQueryOptions(diaryId, locale),
        enabled: enabled && Boolean(diaryId),
    });
};

export const useDiariesByPatientQuery = (patientId: string) => {
    return useSuspenseQuery(diariesByPatientQueryOptions(patientId));
};

export const useDiariesTabsQuery = (filters: Record<string, unknown> = {}) => {
    return useSuspenseQuery(diariesTabsQueryOptions(filters));
};
